import { useCallback, useEffect, useRef, useState } from "react";
import { themeVar as colors } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import {
  EMPTY_BRANDING,
  MAX_LOGO_BYTES,
  getBranding,
  putBranding,
  type Branding,
  type LogoPlacement,
  type ThemePreset,
} from "../lib/brandingApi";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import { blockStyle } from "../ui/Block";
import { PageHead } from "../ui/PageHead";

/**
 * REPORT BRANDING — `/branding`.
 *
 * The `/next` port of the legacy app's `#sec-branding`. Every field here already reaches the
 * deliverable: `amb_report._run_job` loads the row into `meta.branding` on every build, and the
 * renderer reads the firm name, preparer, licence, contact, logo, palette and the two bios from it.
 * Nothing new is wired into the report by this screen — it is the missing way to SET what the
 * report already reads.
 *
 * WHY IT IS A PREREQUISITE FOR THE APEX SWAP, not a nice-to-have. Right now the only branding form
 * lives in the legacy app. When `valubrief.ai` starts serving this client, that form is on a
 * hostname nobody visits, and every brief keeps rendering with whatever was last saved through it —
 * with no way to correct it from inside the app the agent is actually using. Wrong cover data is a
 * branding RECORD problem, and this is the screen that fixes records.
 *
 * WHAT IS DELIBERATELY THE SERVER'S, NOT THIS SCREEN'S:
 *
 *   * The theme presets and the placement options both come from `GET /api/branding`, never from a
 *     table in this file. `client/src/cma/constants.ts` is the cautionary tale — a hardcoded second
 *     copy of the persona registry that drifted from the server's for weeks, so a rename shipped as
 *     a no-op for the only person it was for. Presets are exactly the same shape of data.
 *   * Palette extraction, white knockout and SVG-to-PNG conversion all happen in `put_branding`.
 *     This screen sends the raw data URI and re-reads what the server stored, so what is on screen
 *     after a save is what a report will actually use — not what the browser hoped it would be.
 *   * The 2 MB cap. The check below is a courtesy that saves a round trip; `app.MAX_LOGO_CHARS` is
 *     the real gate, and its 400 is surfaced verbatim rather than being second-guessed here.
 */
export function BrandingScreen() {
  useDocumentTitle(vbTitle("Branding"));
  const [form, setForm] = useState<Branding>(EMPTY_BRANDING);
  const [presets, setPresets] = useState<ThemePreset[]>([]);
  const [placements, setPlacements] = useState<LogoPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getBranding()
      .then((payload) => {
        setForm({ ...EMPTY_BRANDING, ...payload.branding });
        setPresets(payload.presets ?? []);
        setPlacements(payload.placements ?? []);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't load your branding."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  function set<K extends keyof Branding>(key: K, value: Branding[K]) {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleLogo(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      setError("That logo is over 2 MB. Save a smaller copy and try again.");
      ev.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setError(null);
      set("logo_data_uri", typeof reader.result === "string" ? reader.result : null);
    };
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // Re-read what the SERVER stored rather than trusting the form. put_branding rasterises an
      // SVG, extracts the palette and builds the white knockout, so the stored row is not the row
      // that was sent — and what is on screen afterwards should be what a report will use.
      const r = await putBranding({
        firm_name: form.firm_name?.trim() || null,
        preparer_name: form.preparer_name?.trim() || null,
        license_no: form.license_no?.trim() || null,
        contact: form.contact?.trim() || null,
        logo_data_uri: form.logo_data_uri,
        logo_placement: form.logo_placement,
        theme_preset: form.theme_preset,
        show_valubrief_logo: form.show_valubrief_logo,
        agent_bio: form.agent_bio?.trim() || null,
        agent_bio_verbatim: form.agent_bio_verbatim,
        company_bio: form.company_bio?.trim() || null,
        company_bio_verbatim: form.company_bio_verbatim,
      });
      setForm({ ...EMPTY_BRANDING, ...r.branding });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save your branding.");
    } finally {
      setSaving(false);
    }
  }

  const label: React.CSSProperties = {
    display: "block", fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 6,
  };
  const input: React.CSSProperties = {
    width: "100%", padding: "9px 11px", borderRadius: 8,
    border: `1px solid ${colors.border}`, background: colors.bgSurface,
    color: colors.textPrimary, fontSize: 14, fontFamily: "inherit",
  };
  const row: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };
  const hint: React.CSSProperties = { fontSize: 12, color: colors.textSecondary, marginTop: 4 };

  return (
    /* `content="form"` (1200), NOT the default. This screen is no longer a single column of prose:
       it is a form beside a preview, which is the arrangement `measures.ts` says the `form` measure
       exists for. Left at the default it inherited an 880px cap and the second column had nowhere
       to go — measured at 1512, the whole page rendered 880px wide with 316px of empty grey on each
       side, which is the exact figure that note records as the defect. */
    <AppShell step="branding" content="form">
      {/* THE SCREEN IS TWO COLUMNS NOW, AND THE SECOND ONE IS THE POINT  (2026-08-31, owner: "the
          branding section absolutely needs to be brought to the new UI experience").

          WHAT THIS SCREEN WAS. One 880px column of form rows, centred, on a full-bleed page — the
          exact arrangement `ui/measures.ts` already measured as 58% of the glass used at 1512 and
          46% at 1920. It was also the ONE screen in the app that centred its block while every
          other screen hung on the left, so moving to it or away from it visibly shifted the page.
          And every heading, label and hint carried its own hand-written pixel size.

          WHAT IT IS NOW. `PageHead` for the header, so this screen opens exactly the way every
          other screen opens. `Band` for the body, so the form keeps a readable measure while the
          width that used to sit empty beside it carries a LIVE COVER PREVIEW — which is the real
          upgrade rather than the layout. This screen sets what prints on a cover, and until today
          the only way to see the result was to build a whole brief. The preview is not a promise
          about the renderer: it shows the fields THIS FORM holds, so it can be exactly honest about
          them, and it says so underneath rather than implying it is a proof.

          `Band` unstacks on its CONTAINER, so on a narrow laptop the preview drops below the form
          in source order and nothing is crushed. No media query, no viewport assumption. */}
      <div style={{ ...blockStyle("form"), padding: "8px 0 48px" }}>
        <PageHead
          title="Report branding"
          sub={<>Set it once. Every brief you generate leads with your firm, and these details print
                on the cover and in the note before your signature.</>}
          actions={!loading && (
            <>
              {saved && (
                <span role="status" style={{ fontSize: 13, color: colors.accent }}>
                  Saved. Every brief from here on uses this.
                </span>
              )}
              <button className="vb-btn" type="button" onClick={handleSave} disabled={saving}
                      style={{ padding: "9px 18px", borderRadius: 8, border: "none",
                               background: colors.accent, color: "#fff", fontSize: 14,
                               fontWeight: 600, cursor: saving ? "default" : "pointer",
                               opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving…" : "Save branding"}
              </button>
            </>
          )}
        />

        {loading && <p style={{ fontSize: 14, color: colors.textSecondary }}>Loading…</p>}

        {!loading && (
          /* A FIXED PREVIEW COLUMN, NOT TWO EQUAL ONES. `Band` gives two `1fr` columns, which is
             right when both sides are content and wrong here: the sheet's width is set by its
             aspect ratio and a sensible page height, so in a 586px column it either sat 226px
             short of the edge or grew to a 758px-tall page nobody can see at once. The form takes
             whatever is left, which is also the correct priority — the form is what someone came
             to use. Collapses to one column below 900px on its own container. */
          <div className="vb-brand-layout">
            <div>
            <div style={row}>
              <div>
                <label style={label} htmlFor="firm_name">Firm name</label>
                <input id="firm_name" style={input} value={form.firm_name ?? ""}
                       onChange={(e) => set("firm_name", e.target.value)} />
              </div>
              <div>
                <label style={label} htmlFor="preparer_name">Preparer name</label>
                <input id="preparer_name" style={input} value={form.preparer_name ?? ""}
                       onChange={(e) => set("preparer_name", e.target.value)} />
              </div>
            </div>

            <div style={{ ...row, marginTop: 14 }}>
              <div>
                <label style={label} htmlFor="license_no">License number</label>
                <input id="license_no" style={input} value={form.license_no ?? ""}
                       onChange={(e) => set("license_no", e.target.value)} />
                {/* The 2026-08-08 brief printed "TX License admin-test" on a client cover. That was
                    this field holding a test value, not a rendering defect, which is exactly why
                    this screen has to exist somewhere an agent will find it. */}
                <p style={hint}>Prints on the cover, next to your name.</p>
              </div>
              <div>
                <label style={label} htmlFor="contact">Contact (email or phone)</label>
                <input id="contact" style={input} value={form.contact ?? ""}
                       onChange={(e) => set("contact", e.target.value)} />
              </div>
            </div>

            <div style={{ marginTop: 22 }}>
              <label style={label} htmlFor="logo">Logo <span style={{ fontWeight: 400, color: colors.textSecondary }}>(PNG, JPG or SVG — optional)</span></label>
              <input id="logo" ref={fileRef} type="file" style={{ ...input, padding: 8 }}
                     accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogo} />
              {form.logo_data_uri && (
                <div style={{ marginTop: 10 }}>
                  <img src={form.logo_data_uri} alt="Your logo, as your reports will use it"
                       style={{ maxHeight: 56, maxWidth: 240, border: `1px solid ${colors.border}`,
                                borderRadius: 6, padding: 4, background: "#fff" }} />
                  <div>
                    <button type="button" onClick={() => { set("logo_data_uri", null); if (fileRef.current) fileRef.current.value = ""; }}
                            style={{ marginTop: 8, padding: "5px 10px", borderRadius: 8, fontSize: 12,
                                     border: `1px solid ${colors.border}`, background: colors.bgSurface,
                                     color: colors.textPrimary, cursor: "pointer" }}>
                      Remove logo
                    </button>
                  </div>
                </div>
              )}
              <p style={hint}>
                Your report colors are matched to this logo automatically. An SVG is converted on
                save, because Word cannot embed one.
              </p>
            </div>

            <div style={{ ...row, marginTop: 22 }}>
              <div>
                <label style={label} htmlFor="theme_preset">Theme</label>
                <select id="theme_preset" style={input} value={form.theme_preset ?? ""}
                        onChange={(e) => set("theme_preset", e.target.value || null)}>
                  <option value="">Match my logo colors (default)</option>
                  {presets.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={label} htmlFor="logo_placement">Logo placement</label>
                {/* `?? "strip"` shows the default when nothing is stored, and the state stays
                    null until the agent picks something. That is not a mismatch: `resolve_branding`
                    reads `b.get("logo_placement") or "strip"` (amb_build.py:2430), so null and
                    "strip" produce the identical report. Written down because the shown value and
                    the sent value differing is normally a bug, and here it provably is not. */}
                <select id="logo_placement" style={input} value={form.logo_placement ?? "strip"}
                        onChange={(e) => set("logo_placement", e.target.value as LogoPlacement)}>
                  {placements.map((p) => (
                    <option key={p} value={p}>{PLACEMENT_LABEL[p] ?? p}</option>
                  ))}
                </select>
                {/* VB-37: a logo sitting in the panel above with placement set to "off" is two
                    controls giving opposite answers, and the one that wins is invisible until a
                    brief is delivered without the firm's mark on it. Deliberately narrow: it only
                    fires on an EXPLICIT "off". A null placement is not this case — `resolve_branding`
                    reads `b.get("logo_placement") or "strip"` (amb_build.py:2430), so a fresh
                    upload that has never touched this select does print. Stated, not corrected:
                    silently re-pointing the select would overwrite a choice the agent may have
                    made on purpose. */}
                {form.logo_data_uri && form.logo_placement === "off" && (
                  <p role="status" style={{ margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.5,
                                            color: colors.caution }}>
                    Your logo is saved but won&apos;t print — placement is set to &ldquo;No logo&rdquo;.
                    Choose one of the other placements to use it.
                  </p>
                )}
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, fontSize: 13 }}>
              <input type="checkbox" checked={form.show_valubrief_logo}
                     onChange={(e) => set("show_valubrief_logo", e.target.checked)} />
              Show the ValuBrief logo when I haven’t added my own
            </label>

            <h2 style={{ fontSize: 16, fontWeight: 600, marginTop: 30, marginBottom: 2 }}>
              About you and your brokerage <span style={{ fontWeight: 400, fontSize: 14, color: colors.textSecondary }}>(optional)</span>
            </h2>
            <p style={{ ...hint, marginTop: 0, marginBottom: 14 }}>
              Added to the end of the note before your signature. Left unticked, we tidy it into
              clean professional prose. We never add anything you did not write.
            </p>

            <label style={label} htmlFor="agent_bio">About you</label>
            <textarea id="agent_bio" rows={3} style={{ ...input, resize: "vertical" }}
                      value={form.agent_bio ?? ""} onChange={(e) => set("agent_bio", e.target.value)} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 13 }}>
              <input type="checkbox" checked={form.agent_bio_verbatim}
                     onChange={(e) => set("agent_bio_verbatim", e.target.checked)} />
              Use my exact words
            </label>

            <label style={{ ...label, marginTop: 18 }} htmlFor="company_bio">About your brokerage</label>
            <textarea id="company_bio" rows={3} style={{ ...input, resize: "vertical" }}
                      value={form.company_bio ?? ""} onChange={(e) => set("company_bio", e.target.value)} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 13 }}>
              <input type="checkbox" checked={form.company_bio_verbatim}
                     onChange={(e) => set("company_bio_verbatim", e.target.checked)} />
              Use my exact words
            </label>

            {/* ONE SAVE BUTTON, AND IT IS IN THE HEADER. A second copy at the foot was drafted for
                the long-scroll case and removed: two controls with the same name is an ambiguity
                for anything navigating by role — a screen reader, a keyboard user, a test — and the
                two-column layout made the scroll it was solving for much shorter anyway. The header
                is also where every other screen's primary action now sits, which is the point of
                having a shared header at all. */}
            </div>

            <CoverPreview form={form} presets={presets} />
          </div>
        )}

        {error && (
          <p role="alert" style={{ marginTop: 16, fontSize: 13, color: "#B91C1C" }}>
            {error}
          </p>
        )}
      </div>
    </AppShell>
  );
}

/**
 * Labels only. The KEYS come from the server (`app._LOGO_PLACEMENTS`) — an option the server does
 * not offer is never rendered, and one it adds shows up with its raw key rather than disappearing.
 */
const PLACEMENT_LABEL: Record<string, string> = {
  strip: "Above the banner (works with any logo)",
  banner: "Inside the header banner (best for a transparent dark logo)",
  off: "No logo",
};

/* --------------------------------------------------------------------------- *
 * THE LIVE COVER PREVIEW                                                        *
 * --------------------------------------------------------------------------- */

/**
 * What the form's fields will look like on a cover, updated as they are typed.
 *
 * WHY THIS IS WORTH BUILDING AND WHY IT IS BOUNDED. Until today the only way to see the effect of
 * this screen was to build a whole brief — so the "TX License admin-test" that reached a client
 * cover on 2026-08-08 was invisible right up to the moment it was delivered. A preview is the
 * cheapest possible guard against that class of mistake, because the mistake is always a field
 * holding the wrong words rather than a renderer behaving badly.
 *
 * IT IS NOT A RENDER, AND IT SAYS SO. The real cover comes out of `amb_build`, through WeasyPrint,
 * with a banner, a subject photograph, a valuation and a whole document behind it. This draws the
 * five fields THIS FORM owns, in the arrangement the cover puts them in, and nothing else. That
 * distinction is stated on screen rather than left for someone to discover: a preview that implied
 * it was the deliverable would be this product making exactly the kind of confident-looking claim
 * it exists to refuse. Calling it "the details this screen controls" is accurate; calling it "your
 * cover" would not be.
 *
 * THE COLOURS ARE THE SERVER'S OR THEY ARE NOTHING. `put_branding` extracts a palette from the
 * uploaded logo, and this client cannot and must not guess at what it will extract. So a chosen
 * preset shows the preset's NAME, an unchosen one says the palette will be matched to the logo on
 * save, and neither invents a swatch. Unknown is never a colour picked to look plausible.
 */
function CoverPreview({ form, presets }: { form: Branding; presets: ThemePreset[] }) {
  const firm = form.firm_name?.trim();
  const preparer = form.preparer_name?.trim();
  const license = form.license_no?.trim();
  const contact = form.contact?.trim();
  const preset = presets.find((p) => p.key === form.theme_preset);
  const logoPrints = !!form.logo_data_uri && form.logo_placement !== "off";

  return (
    <aside className="vb-brand-preview" aria-label="How these details will appear">
      <p className="vb-brand-preview-label">On the cover</p>

      <div className="vb-brand-sheet">
        {/* The mark, where "Above the banner" puts it. A logo set to "No logo" is deliberately not
            drawn here — the panel above already carries the warning, and drawing it anyway would
            make this preview contradict that warning on the same screen. */}
        <div className="vb-brand-sheet-mark">
          {logoPrints
            ? <img src={form.logo_data_uri ?? undefined} alt="" />
            : <span className="vb-brand-sheet-empty">
                {form.show_valubrief_logo ? "ValuBrief mark" : "no logo"}
              </span>}
        </div>

        <div className="vb-brand-sheet-band" />

        {/* EVERY FIELD FALLS BACK TO A NAMED ABSENCE, NEVER TO A PLACEHOLDER THAT LOOKS REAL. A
            greyed "Your firm name" in the firm slot would be a preview of a cover nobody will ever
            get. The em-dash-with-a-reason convention says what is missing instead. */}
        <p className="vb-brand-sheet-firm">
          {firm || <span className="vb-brand-sheet-blank">— no firm name set</span>}
        </p>
        <p className="vb-brand-sheet-line">
          {preparer || <span className="vb-brand-sheet-blank">— no preparer name set</span>}
          {license ? <> · TX License {license}</> : null}
        </p>
        {contact && <p className="vb-brand-sheet-line">{contact}</p>}
      </div>

      <dl className="vb-brand-facts">
        <div>
          <dt>Colours</dt>
          <dd>{preset ? preset.name : "Matched to your logo when you save"}</dd>
        </div>
        <div>
          <dt>Logo</dt>
          <dd>
            {!form.logo_data_uri
              ? (form.show_valubrief_logo ? "The ValuBrief mark, until you add yours" : "None")
              : form.logo_placement === "off"
                ? "Saved, but set never to print"
                : form.logo_placement === "banner"
                  ? "Inside the header banner"
                  : "Above the banner"}
          </dd>
        </div>
      </dl>

      <p className="vb-brand-preview-note">
        This shows the details this screen controls, in the order the cover puts them. The finished
        cover is built by the report renderer and also carries the banner, the property photograph
        and the valuation.
      </p>
    </aside>
  );
}
